/**
 * Sysadmin Catalog → Skills (PR-I).
 *
 * List view for system skills with:
 *   - filter chips (status, category)
 *   - search by name/slug/description
 *   - cursor-paginated table (Load more button)
 *   - "New Skill" button → create drawer
 *
 * Detail view at /sysadmin/catalog/skills/$id (catalog.skills.$id.tsx).
 *
 * Lifecycle: every skill row carries a status pill (draft / published /
 * deprecated / retired). Tenants only see published-AND-assigned skills.
 *
 * Mirrors models.tsx + catalog.prompts.tsx patterns. No mock data:
 * empty list shows the empty state, errors show the error card.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Sparkles, Search, AlertTriangle, Plus, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  SkillContentEditor,
  type SkillContentDraft,
} from '@/components/skill-editor-shared/SkillContentEditor';
import {
  useCatalogSkills,
  useCreateCatalogSkill,
  type SkillStatus,
} from '@/lib/use-sysadmin-catalog-skills';

// R5 P0-1 fix (2026-05-13): list now lives under index route ID
// `/sysadmin/catalog/skills/`; parent `catalog.skills.tsx` is a pure
// layout with <Outlet/> so detail children can render.
export const Route = createFileRoute('/sysadmin/catalog/skills/')({
  component: SysadminCatalogSkillsPage,
});

const STATUS_FILTERS: Array<{ key: SkillStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'deprecated', label: 'Deprecated' },
  { key: 'retired', label: 'Retired' },
];

function statusPillClass(status: SkillStatus): string {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'draft':
      return 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30';
    case 'deprecated':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'retired':
      return 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-400 border-zinc-500/30';
  }
}

function SysadminCatalogSkillsPage() {
  const [statusFilter, setStatusFilter] = useState<SkillStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const list = useCatalogSkills({
    status: statusFilter === 'all' ? undefined : statusFilter,
    category: categoryFilter || undefined,
    search: search.trim() || undefined,
    limit: 50,
  });

  const allItems = useMemo(
    () => list.data?.pages.flatMap((p) => p.items) ?? [],
    [list.data],
  );

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((s) => s.category && set.add(s.category));
    return Array.from(set).sort();
  }, [allItems]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Sparkles className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Skill Library</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">§3.3</Badge>
            <Badge variant="secondary" className="text-xs">v1</Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Catalog</CardTitle>
          <CardDescription>
            Sysadmin-authored skill bundles. A skill composes one or more
            tools and/or functions plus an instruction body. Tenants only
            see skills with <span className="font-mono">status=published</span>{' '}
            and an active assignment row.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Filters + search + new button */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Status:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs border transition-colors',
                statusFilter === f.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:border-foreground/40',
              )}
              title={`Filter by ${f.label.toLowerCase()}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {categoryOptions.length > 0 && (
            <>
              <span className="text-xs text-muted-foreground mr-1">Category:</span>
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  !categoryFilter
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:border-foreground/40',
                )}
              >
                All
              </button>
              {categoryOptions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoryFilter(c)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs border transition-colors',
                    categoryFilter === c
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:border-foreground/40',
                  )}
                >
                  {c}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by name, slug, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <Button
            size="sm"
            className="gap-1.5 ml-auto"
            onClick={() => setShowCreate(true)}
            title="Create a new draft skill"
          >
            <Plus className="h-3.5 w-3.5" /> New Skill
          </Button>
        </div>
      </div>

      {list.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading skills…
          </CardContent>
        </Card>
      ) : list.error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {list.error.message}</div>
          </CardContent>
        </Card>
      ) : allItems.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No skills match the current filters.
            {(statusFilter !== 'all' || categoryFilter || search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setCategoryFilter('');
                  setSearch('');
                }}
                className="ml-2"
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32">Slug</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  <TableHead className="w-20 text-right">Tools</TableHead>
                  <TableHead className="w-20 text-right">Functions</TableHead>
                  <TableHead className="w-20 text-right">Version</TableHead>
                  <TableHead className="w-28 text-right">Tenants</TableHead>
                  <TableHead className="w-44">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell>
                      <Link
                        to="/sysadmin/catalog/skills/$id"
                        params={{ id: s.id }}
                        className="block"
                      >
                        <div className="text-sm font-medium">{s.displayName}</div>
                        {s.description && (
                          <div className="text-[11px] text-muted-foreground line-clamp-1 max-w-md">
                            {s.description}
                          </div>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/sysadmin/catalog/skills/$id"
                        params={{ id: s.id }}
                        className="font-mono text-xs"
                      >
                        {s.slug}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] capitalize border',
                          statusPillClass(s.status),
                        )}
                      >
                        {s.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {s.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {s.toolDependencies.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {s.functionDependencies.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      v{s.version}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {s.assignmentCount}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(s.updatedAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {list.hasNextPage && (
              <div className="border-t px-4 py-3 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      Loading more…
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CreateSkillSheet
        open={showCreate}
        onOpenChange={setShowCreate}
      />
    </div>
  );
}

// ─── Create drawer ────────────────────────────────────────────────────────
//
// PR-W22B (2026-05-23): the form body (Identity + Description + Instruction
// body + Skill Helper) is now rendered by the shared <SkillContentEditor
// mode="create"> in `@/components/skill-editor-shared/SkillContentEditor`.
// This file owns the create-flow chrome (Sheet, header, Cancel/Create
// buttons, mutation) and delegates everything else to the shared editor.
// Closes #587 for the catalog list surface — the catalog detail Overview
// tab mounts the same shared editor with mode="edit".

const EMPTY_CREATE: SkillContentDraft = {
  slug: '',
  displayName: '',
  description: '',
  instructions: '',
  category: 'general',
};

function CreateSkillSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [draft, setDraft] = useState<SkillContentDraft>(EMPTY_CREATE);
  const create = useCreateCatalogSkill();

  const handleCreate = async () => {
    if (!draft.slug || !draft.displayName || !draft.description || !draft.instructions) {
      return;
    }
    try {
      await create.mutateAsync({
        slug: draft.slug,
        displayName: draft.displayName,
        description: draft.description,
        instructions: draft.instructions,
        category: draft.category || 'general',
        toolDependencies: [],
        functionDependencies: [],
      });
      toast.success(`Created draft "${draft.displayName}"`);
      setDraft(EMPTY_CREATE);
      onOpenChange(false);
    } catch (err) {
      toast.error('Create failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Skill</SheetTitle>
          <SheetDescription>
            Creates a draft skill. Add tool / function references and refine
            the instruction body on the detail page, then publish from the
            lifecycle action bar.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          <SkillContentEditor
            mode="create"
            draft={draft}
            onDraftChange={setDraft}
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_CREATE);
                onOpenChange(false);
              }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={
                !draft.slug ||
                !draft.displayName ||
                !draft.description ||
                !draft.instructions ||
                create.isPending
              }
            >
              {create.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create draft'
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
